import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

/**
 * A custom element that displays an image marquee with swiper-like functionality.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 *
 * @extends Component<Refs>
 */
class ImageMarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content'];

  connectedCallback() {
    super.connectedCallback();

    const { content } = this.refs;
    if (content.firstElementChild?.children.length === 0) return;

    // iOS Safari optimizations
    this.#addIOSOptimizations();
    
    this.#addRepeatedItems();
    this.#duplicateContent();
    this.#setSpeed();

    window.addEventListener('resize', this.#handleResize);
    
    // Add hover pause functionality
    if (this.getAttribute('data-pause-on-hover') === 'true') {
      this.addEventListener('pointerenter', this.#pauseAnimation);
      this.addEventListener('pointerleave', this.#resumeAnimation);
    }

    // Add touch/swipe support for mobile
    this.#addTouchSupport();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
    this.removeEventListener('pointerenter', this.#pauseAnimation);
    this.removeEventListener('pointerleave', this.#resumeAnimation);
    this.#removeTouchSupport();
  }

  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  #animation = null;
  #touchStartX = 0;
  #touchStartY = 0;
  #isDragging = false;
  #originalSpeed = 1;

  // iOS Safari optimizations
  #addIOSOptimizations() {
    // Detect iOS Safari
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
    
    if (isIOS && isSafari) {
      // Add iOS-specific CSS classes and optimizations
      this.classList.add('ios-safari');
      
      // Force hardware acceleration
      this.style.setProperty('-webkit-transform', 'translateZ(0)');
      this.style.setProperty('transform', 'translateZ(0)');
      this.style.setProperty('-webkit-backface-visibility', 'hidden');
      this.style.setProperty('backface-visibility', 'hidden');
      
      // Optimize for iOS Safari's rendering engine
      this.style.setProperty('-webkit-overflow-scrolling', 'touch');
      this.style.setProperty('overflow-scrolling', 'touch');
      
      // Reduce animation complexity on iOS
      const speedFactor = Number(this.getAttribute('data-speed-factor'));
      if (speedFactor < 15) {
        this.setAttribute('data-speed-factor', '15');
      }
    }
  }

  #pauseAnimation = debounce(() => {
    if (this.#animation) return;

    const animation = this.refs.wrapper.getAnimations()[0];
    if (!animation) return;

    this.#originalSpeed = animation.playbackRate;
    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: this.#originalSpeed,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  #resumeAnimation() {
    this.#pauseAnimation.cancel();

    const animation = this.refs.wrapper.getAnimations()[0];
    if (!animation || animation.playbackRate === this.#originalSpeed) return;

    const from = this.#animation?.current ?? 0;
    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: this.#originalSpeed,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;
    return content !== lastChild ? lastChild : null;
  }

  #setSpeed(value = this.#calculateSpeed()) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  #calculateSpeed() {
    const speedFactor = Number(this.getAttribute('data-speed-factor'));
    const marqueeWidth = this.offsetWidth;
    const speed = Math.ceil(marqueeWidth / speedFactor / 2);
    return speed;
  }

  #handleResize = debounce(() => {
    const { content } = this.refs;
    const newNumberOfCopies = this.#calculateNumberOfCopies();
    const currentNumberOfCopies = content.children.length;

    if (newNumberOfCopies > currentNumberOfCopies) {
      this.#addRepeatedItems(newNumberOfCopies - currentNumberOfCopies);
    } else if (newNumberOfCopies < currentNumberOfCopies) {
      this.#removeRepeatedItems(currentNumberOfCopies - newNumberOfCopies);
    }

    this.#duplicateContent();
    this.#setSpeed();
    this.#restartAnimation();
  }, 250);

  #restartAnimation() {
    const animations = this.refs.wrapper.getAnimations();
    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');
    this.refs.wrapper.appendChild(clone);
  }

  #addRepeatedItems(numberOfCopies = this.#calculateNumberOfCopies()) {
    const { content } = this.refs;
    const wrapper = content.firstElementChild;

    if (!wrapper) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = wrapper.cloneNode(true);
      content.appendChild(clone);
    }
  }

  #removeRepeatedItems(numberOfCopies = this.#calculateNumberOfCopies()) {
    const { content } = this.refs;

    for (let i = 0; i < numberOfCopies; i++) {
      content.lastElementChild?.remove();
    }
  }

  #calculateNumberOfCopies() {
    const { content } = this.refs;
    const marqueeWidth = this.offsetWidth;
    const marqueeRepeatedItemWidth =
      content.firstElementChild instanceof HTMLElement ? content.firstElementChild.offsetWidth : 1;

    return marqueeRepeatedItemWidth === 0 ? 1 : Math.ceil(marqueeWidth / marqueeRepeatedItemWidth);
  }

  // Touch/Swipe support for mobile devices
  #addTouchSupport() {
    // iOS Safari specific touch handling
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    
    if (isIOS) {
      // Use more conservative touch handling for iOS
      this.addEventListener('touchstart', this.#handleTouchStart, { passive: false });
      this.addEventListener('touchmove', this.#handleTouchMove, { passive: false });
      this.addEventListener('touchend', this.#handleTouchEnd, { passive: true });
    } else {
      // Standard touch handling for other devices
      this.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
      this.addEventListener('touchmove', this.#handleTouchMove, { passive: true });
      this.addEventListener('touchend', this.#handleTouchEnd, { passive: true });
    }
  }

  #removeTouchSupport() {
    this.removeEventListener('touchstart', this.#handleTouchStart);
    this.removeEventListener('touchmove', this.#handleTouchMove);
    this.removeEventListener('touchend', this.#handleTouchEnd);
  }

  #handleTouchStart = (event) => {
    if (!event.touches || event.touches.length === 0) return;
    this.#touchStartX = event.touches[0].clientX;
    this.#touchStartY = event.touches[0].clientY;
    this.#isDragging = false;
  };

  #handleTouchMove = (event) => {
    if (!this.#touchStartX || !this.#touchStartY) return;
    if (!event.touches || event.touches.length === 0) return;

    const touchX = event.touches[0].clientX;
    const touchY = event.touches[0].clientY;
    const diffX = Math.abs(touchX - this.#touchStartX);
    const diffY = Math.abs(touchY - this.#touchStartY);

    // Determine if this is a horizontal swipe
    if (diffX > diffY && diffX > 10) {
      this.#isDragging = true;
      
      // iOS Safari specific handling
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      if (isIOS) {
        // More aggressive prevention for iOS Safari
        event.preventDefault();
        event.stopPropagation();
        
        // Pause animation during touch on iOS
        const animation = this.refs.wrapper.getAnimations()[0];
        if (animation && animation.playbackRate > 0) {
          animation.pause();
        }
      } else {
        event.preventDefault();
      }
    }
  };

  #handleTouchEnd = (event) => {
    if (!this.#isDragging) return;
    if (!event.changedTouches || event.changedTouches.length === 0) return;

    const touchEndX = event.changedTouches[0].clientX;
    const diffX = touchEndX - this.#touchStartX;
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

    // Resume animation if it was paused during touch on iOS
    if (isIOS) {
      const animation = this.refs.wrapper.getAnimations()[0];
      if (animation && animation.playState === 'paused') {
        animation.play();
      }
    }

    // Adjust animation speed based on swipe direction and velocity
    if (Math.abs(diffX) > 50) {
      const animation = this.refs.wrapper.getAnimations()[0];
      if (animation) {
        const direction = this.getAttribute('data-movement-direction');
        const isReverse = direction === 'reverse';
        const shouldSlowDown = (diffX > 0 && !isReverse) || (diffX < 0 && isReverse);
        
        if (shouldSlowDown) {
          this.#pauseAnimation();
          setTimeout(() => this.#resumeAnimation(), 1000);
        }
      }
    }

    this.#touchStartX = 0;
    this.#touchStartY = 0;
    this.#isDragging = false;
  };
}

// Define the animateValue function
/**
 * Animate a numeric property smoothly.
 * @param {Object} params - The parameters for the animation.
 * @param {number} params.from - The starting value.
 * @param {number} params.to - The ending value.
 * @param {number} params.duration - The duration of the animation in milliseconds.
 * @param {function(number): void} params.onUpdate - The function to call on each update.
 * @param {function(number): number} [params.easing] - The easing function.
 * @param {function(): void} [params.onComplete] - The function to call when the animation completes.
 */
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  /**
   * @param {number} currentTime - The current time in milliseconds.
   */
  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get('image-marquee-component')) {
  customElements.define('image-marquee-component', ImageMarqueeComponent);
}

